import Vue from 'vue';
import Router from 'vue-router';
import {store} from '../store/store';
import storeActions from '../store/actions';
import {findByKey} from '../helpers/index';

const payments = () => import(/* webpackPrefetch: true webpackChunkName: "payments" */ '../templates/payments/payments');
const payment = () => import(/* webpackChunkName: "payments" */ '../templates/payments/payment');
const techVisits = () => import(/* webpackChunkName: "techVisits" */ '../templates/tech_visits/techVisits.vue');
const techVisit = () => import(/* webpackChunkName: "techVisits" */ '../templates/tech_visits/techVisit');
const thirdPartyPayments = () => import(/* webpackChunkName: "thirdPartyPayments" */ '../templates/third-party-payments/thirdPartyPayments');
const thirdPartyPayment = () => import(/* webpackChunkName: "thirdPartyPayments" */ '../templates/third-party-payments/thirdPartyPayment');
const sessions = () => import(/* webpackChunkName: "sessions" */ '../templates/sessions/sessions');
const session = () => import(/* webpackChunkName: "sessions" */'../templates/sessions/session');
const gwLog = () => import(/* webpackChunkName: "sessions" */'../templates/gw-log/gwLog');
const translations = () => import(/* webpackChunkName: "sessions" */'../templates/translations/translations');
const audit = () => import(/* webpackChunkName: "sessions" */'../templates/audit/audit');
const machines = () => import(/* webpackChunkName: "machines" */ '../templates/machines/machines');
const machine = () => import(/* webpackChunkName: "machines" */ '../templates/machines/machine');
const storeDeposit = () => import(/* webpackChunkName: "storeDeposit" */ '../templates/storeDeposit/storeDeposit');
const storeDepositDetail = () => import(/* webpackChunkName: "storeDeposit" */ '../templates/storeDeposit/storeDepositDetail');
const atmTxs = () => import(/* webpackChunkName: "atm" */ '../templates/atmTransactions/atmTxs');
const atmTx = () => import(/* webpackChunkName: "atm" */'../templates/atmTransactions/atmTx');
const cash = () => import('../templates/cash/cash');
const cashExtraction = () => import('../templates/cash/cash-extraction');
const customers = () => import(/* webpackChunkName: "customers" */ '../templates/customers/customers');
const customer = () => import(/* webpackChunkName: "customers" */ '../templates/customers/customer');
const customerFraudReport = () => import(/* webpackChunkName: "customers" */ '../templates/customers/customerFraudReport');
const shoppingCarts = () => import('../templates/mobile-cart/shoppingCarts');
const page404 = () => import('../templates/404');
const alerts = () => import(/* webpackChunkName: "alerts" */ '../templates/alerts/alerts');
const alertsExport = () => import(/* webpackChunkName: "alerts" */ '../templates/alerts/alertsExport');
const alert = () => import(/* webpackChunkName: "alerts" */ '../templates/alerts/alert');
const dashboard = () => import('../templates/dashboard/dashboard');
const map = () => import(/* webpackChunkName: "locations" */ '../templates/locations/map');
const locationsPage = () => import(/* webpackChunkName: "locations" */ '../templates/locations/locationsPage');
const locationPage = () => import(/* webpackChunkName: "locations" */ '../templates/locations/locationPage');
const locations = () => import(/* webpackChunkName: "locations" */ '../templates/locations/locations');
const otcDevices = () => import('../templates/locations/retailers/otcDevices');
const categories = () => import('../templates/categories/categories');
const fileStorage = () => import('../templates/file_storage/fileStorage.vue');
const conversations = () => import('../templates/conversations/conversations');
const partners = () => import('../templates/partners/partners');
const loginGuard = () => import('../templates/loginGuard');
const locationsContainer = () => import('../templates/locations/locationsContainer');
const billers = () => import('../templates/billers/billers');
const promotion = () => import('../templates/promotion/Promotion');
const ticketList = () => import('../templates/promotion/ticketList');
const home = () => import('../templates/home/home');
const sso = () => import('../templates/sso-redirecting/sso');
const deviceClerk = () => import('../templates/devices/clerkDevices');
const permissions = () => import('../templates/permissions/permissions');
const signUpForms = () => import('../templates/sign-up-forms/sign-up-forms');
const signUpDetails = () => import('../templates/sign-up-forms/signUpDetails');
const tools = () => import('../templates/tools/tools');

Vue.use(Router);
export const beforeEnterGuard = (to, from, next) => {
  const routeItem = to.matched.filter(item => item.meta.name);
  if (routeItem && routeItem.length) {
    const name = routeItem[0].meta.name;

    if (store.state.users[0].partner_id === 0) return next();
    else if (name === 'permissions') return next({path: '/404'});
    else if (name === 'storeDeposit' || name === 'storeDepositDetailPage') {
      // storeDepositDetailPage has no matching key under permissions (findByKey
      // below would return null and fall through to an ungated `next()`), so it
      // needs its own check here rather than relying on the generic lookup -
      // otherwise the detail page stays reachable by direct URL even when the
      // list tab itself is hidden.
      const localPermission = findByKey(store.state.users[0].permissions, 'storeDeposit');
      const localVisible = _.isObject(localPermission) ? !!localPermission.visible : !!localPermission;
      return localVisible ? next() : next({path: '/404'});
    }

    const userPermissions = store.state.users[0].permissions;

    const value = findByKey(userPermissions, name);
    if (!_.isNull(value)) {
      if (_.isObject(value)) {
        if (value.visible) {
          next();
        } else {
          next({path: '/404'});
        }
      } else if (value) {
        next();
      } else {
        next({path: '/404'});
      }
    } else {
      next();
    }
  }
};

const createDoubleBeforeEnterGuard = (firstGuard) => {
  return (to, from, next) => {
    firstGuard(to, from);
    beforeEnterGuard(to, from, next);
  };
};

export const router = new Router({
  mode: 'history',
  scrollBehavior(to, from, savedPosition) {
    return {x: 0, y: 0};
  },
  routes: [
    {
      path: '/home',
      component: home,
      meta: {
        title: 'Home'
      }
    },
    {path: '/sso_login', component: sso},
    {path: '/404', component: page404},
    {
      path: '/',
      component: loginGuard,
      beforeEnter(to, from, next) {
        const token = localStorage.getItem('token');

        if (token) {
          store.dispatch(storeActions.LOG_IN_USER, {access_token: token}).then(() => {
            next();
          }, (error) => {
            console.error(error);
            sessionStorage.setItem('restore_url', _.get(router, 'history.pending.path'));
            next('/home');
          });
        } else {
          sessionStorage.setItem('restore_url', _.get(router, 'history.pending.path'));
          next('/home');
        }
      },
      children: [
        {
          path: '/',
          component: payments,
          meta: {
            name: 'transactions',
            title: 'Txs',
          },
        },
        {
          name: 'transactions',
          path: '/transactions/:tab?',
          component: payments,
          meta: {
            title: 'Txs',
          },
        },
        {
          name: 'transactions',
          path: '/transactions-otc/:tab?',
          component: payments,
          meta: {
            title: 'OTC',
          },
        },
        {
          path: '/transaction/:payment_id/:tab?',
          component: payment,
          meta: {
            name: 'openTransactionDetails',
            title: 'Tx',
          },
          beforeEnter: createDoubleBeforeEnterGuard(to => to.meta.id = to.params.payment_id),
        },
        {
          path: '/machines',
          component: machines,
          meta: {
            name: 'machines',
            title: 'Ms',
          },
          beforeEnter: beforeEnterGuard,
        },
        {
          path: '/machine/:device_id/:tab?',
          component: machine,
          meta: {
            name: 'machineDetailPage',
            title: 'M',
          },
          beforeEnter: createDoubleBeforeEnterGuard(to => to.meta.id = to.params.device_id),
        },
        {
          path: '/store-deposit/:tab?',
          component: storeDeposit,
          meta: {
            name: 'storeDeposit',
            title: 'SD',
          },
          beforeEnter: beforeEnterGuard,
        },
        {
          path: '/store-deposit/detail/:id',
          component: storeDepositDetail,
          meta: {
            name: 'storeDepositDetailPage',
            title: 'SD',
          },
          beforeEnter: createDoubleBeforeEnterGuard(to => to.meta.id = to.params.id),
        },
        {
          path: '/tech-visits/:tab?',
          component: techVisits,
          meta: {
            name: 'techVisits',
            title: 'TV',
          },
          beforeEnter: beforeEnterGuard,
        },
        {
          path: '/tech-visit/:id/:tab?',
          component: techVisit,
          meta: {
            name: 'openTechVisit',
            title: 'TV',
          },
          beforeEnter: createDoubleBeforeEnterGuard(to => to.meta.id = to.params.id),
        },
        {
          path: '/sessions',
          component: sessions,
          meta: {
            name: 'sessions',
            title: 'Ss',
          },
          beforeEnter: beforeEnterGuard,
        },
        {
          path: '/session/:session_id/:tab?',
          component: session,
          meta: {
            name: 'sessions',
            title: 'S',
          },
          beforeEnter: createDoubleBeforeEnterGuard(to => to.meta.id = to.params.session_id),
        },
        {
          path: '/third-party-payments/:tab?',
          component: thirdPartyPayments,
          meta: {
            name: 'thirdPartyPayments',
            title: '3pPs',
          },
          beforeEnter: beforeEnterGuard,
        },
        {
          path: '/third-party-payment/:payment_id/:tab?',
          component: thirdPartyPayment,
          meta: {
            name: 'thirdPartyPayments',
            title: '3pP',
          },
          beforeEnter: createDoubleBeforeEnterGuard(to => to.meta.id = to.params.payment_id),
        },
        {
          path: '/gw-log/:b2b_schema?',
          component: gwLog,
          meta: {
            name: 'gwLog',
          },
          beforeEnter: createDoubleBeforeEnterGuard(to => {
            if (to.params.b2b_schema) to.meta.title = `${to.params.b2b_schema} log`;
            else to.meta.title = 'VL';
          }),
        },
        {
          path: '/audit',
          component: audit,
          meta: {
            name: 'audit',
            title: 'A',
          },
          beforeEnter: beforeEnterGuard,
        },
        {
          path: '/atm-transactions',
          component: atmTxs,
          meta: {
            name: 'atmTransactions',
            title: 'ATM Tx',
          },
          beforeEnter: beforeEnterGuard,
        },
        {
          path: '/atm-transaction/:id/:tab?',
          component: atmTx,
          meta: {
            name: 'atmTransactions',
            title: 'ATM',
          },
          beforeEnter: createDoubleBeforeEnterGuard(to => to.meta.id = to.params.id),
        },
        {
          path: '/cash/:tab?',
          component: cash,
          meta: {
            name: 'cash',
            title: '$',
          },
          beforeEnter: beforeEnterGuard,
        },
        {
          path: '/cash-extraction/:id/:tab?',
          component: cashExtraction,
          meta: {
            name: 'openCashExtractionDetail',
            title: '$ex',
          },
          beforeEnter: beforeEnterGuard,
        },
        {
          path: '/customers/:tab?',
          component: customers,
          meta: {
            name: 'customers',
            title: 'Cs',
          },
          beforeEnter: beforeEnterGuard,
        },
        {
          path: '/customer/:id/fraud-report',
          component: customerFraudReport,
          meta: {
            name: 'customerDetails',
            title: 'Fraud Report',
          },
          beforeEnter: createDoubleBeforeEnterGuard(to => to.meta.id = to.params.id),
        },
        {
          path: '/customer/:id/:tab?',
          component: customer,
          meta: {
            name: 'customerDetails',
            title: 'C',
          },
          beforeEnter: createDoubleBeforeEnterGuard(to => to.meta.id = to.params.id),
        },
        {path: '/mobile-cart', meta: {title: 'MC'}, component: shoppingCarts},
        {
          path: '/alerts',
          component: alerts,
          meta: {
            name: 'alerts',
            title: 'As',
          },
          beforeEnter: beforeEnterGuard,
        },
        {
          path: '/alerts/export',
          component: alertsExport,
          meta: {
            name: 'alertsExport',
            title: 'Alerts Export',
          },
          beforeEnter: beforeEnterGuard,
        },
        {
          path: '/alert/:id',
          component: alert,
          beforeEnter: (to, from, next) => {
            to.meta.title = `A${to.params.id}`;
            next();
          },
        },
        {
          path: '/dashboard',
          component: dashboard,
          meta: {
            name: 'dashboard',
            title: 'DB',
          },
          beforeEnter: beforeEnterGuard,
        },
        {
          path: '/locations/:loc_id?',
          component: locations,
          meta: {
            name: 'locations',
          },
          beforeEnter: createDoubleBeforeEnterGuard(to => {
            if (to.params.loc_id) {
              to.meta.title = 'L';
              to.meta.id = to.params.loc_id;
            } else to.meta.title = `Ls`;
          }),
        },
        {
          path: '/locations-list',
          component: locationsContainer,
          children: [
            {name: 'list', path: '', meta: {title: 'Ls'}, component: locationsPage},
            {name: 'map', path: 'map', meta: {title: 'Lm'}, component: map},
          ],
          meta: {
            name: 'locationsList',
            title: 'Ls',
          },
          beforeEnter: beforeEnterGuard,
        },
        {
          path: '/location/:location_id/:tab?',
          component: locationPage,
          meta: {
            name: 'openLocationPage',
            title: 'L',
          },
          beforeEnter: createDoubleBeforeEnterGuard(to => to.meta.id = to.params.location_id),
        },
        {
          path: '/categories/:category_id?',
          component: categories,
          meta: {
            name: 'categories',
            title: 'Ctg',
          },
          beforeEnter: beforeEnterGuard,
        },
        {
          path: '/file-storage',
          component: fileStorage,
          meta: {
            name: 'fileStorage',
            title: 'FS',
          },
          beforeEnter: beforeEnterGuard,
        },
        {
          path: '/conversations',
          component: conversations,
          meta: {
            name: 'conversations',
            title: 'Cnv',
          },
          beforeEnter: beforeEnterGuard,
        },
        {
          path: '/partners',
          component: partners,
          meta: {
            name: 'partners',
            title: 'Ps',
          },
          beforeEnter: beforeEnterGuard,
        },
        {
          path: '/otc-devices',
          component: otcDevices,
          meta: {
            name: 'otcDevices',
            title: 'OD',
          },
          beforeEnter: beforeEnterGuard
        },
        {
          path: '/clerk/:id/:tab?',
          component: deviceClerk,
          beforeEnter: (to, from, next) => {
            to.meta.title = `Cl${shortID(to.params.id)}`;
            next();
          },
        },
        {
          path: '/promotion',
          component: promotion,
          meta: {
            name: 'promotion',
            title: 'Pr',
          },
          beforeEnter: beforeEnterGuard,
        },
        {
          path: '/promotion-tickets',
          meta: {
            title: 'PT',
          },
          component: ticketList,
        },
        {
          path: '/billers',
          component: billers,
          meta: {
            name: 'addBillers',
            title: 'B',
          },
          beforeEnter: beforeEnterGuard,
        },
        {
          path: '/permissions',
          component: permissions,
          meta: {
            name: 'permissions',
            title: 'Ps',
          },
          beforeEnter: beforeEnterGuard,
        },
        {
          path: '/sign-up-forms',
          component: signUpForms,
          meta: {
            name: 'signUpForms',
            title: 'Sup',
          },
          beforeEnter: beforeEnterGuard,
        },
        {
          path: '/sign-up-forms/:id',
          component: signUpDetails,
          meta: {
            name: 'signUpForms',
            title: 'Sup',
          },
          beforeEnter: createDoubleBeforeEnterGuard(to => to.meta.id = to.params.id),
        },
        {
          path: '/tools',
          component: tools,
          meta: {
            name: 'tools',
            title: 'T',
          },
          beforeEnter: beforeEnterGuard,
        },
        {
          path: '/translations',
          component: translations,
          meta: {
            name: 'translations',
            title: 'Tr',
          },
          beforeEnter: beforeEnterGuard,
        },
        {
          name: 'notFound',
          path: '/404',
          component: page404,
        },
        {
          path: '*',
          component: page404,
        },
      ],
    },
  ],
});


const DEFAULT_TITLE = 'Maya Admin';
let notificationInterval;
router.afterEach((to, from) => {
  Vue.nextTick(() => {
    clearInterval(notificationInterval);

    function setTitle() {
      const prefixVal = Math.min(99, parseInt(window.NOTIFICATION_COUNT, 10));
      const prefix = prefixVal ? `(${prefixVal}) ` : null;

      const main = to.meta.title || null;
      let id = to.meta.id ? shortID(to.meta.id) : '';
      if (to.meta.name === 'machineDetailPage') {
        const d = _.find(_.get(window, 'Vue.$store.state.devices'), {device_id: Number(to.meta.id)});
        if (d) id = shortID(d.short_id);
      }

      const suffix = main ? '- Maya Admin' : DEFAULT_TITLE;

      const title = _.compact([prefix, main + id, suffix]).join(' ');

      document.title = title;
    };

    notificationInterval = setInterval(setTitle, 1000);
    setTitle();
  });
});

function shortID(v) {
  return String(v).slice(-3);
}



// WEBPACK FOOTER //
// ./src/router/router.js